import { useState } from "react";
import { setItem } from "../lib/safeStorage";
import { ScreenshotFrame, ShowcaseScreenshot } from "./LandingVisuals";

const QUICK_TILES = [
  { id: "chat", icon: "💬", title: "Ask Copilot", desc: "L2 answers with runbooks and citations", page: "chat" },
  { id: "logs", icon: "📋", title: "Logs", desc: "Search app, platform, and SRE logs", page: "logs" },
  { id: "kb", icon: "📚", title: "Knowledge", desc: "Upload SOPs and query with RAG", page: "kb" },
  { id: "integrations", icon: "🔗", title: "Integrations", desc: "Splunk, K8s, and connector status", page: "integrations" },
];

const HIGHLIGHTS = [
  "Structured L2 panels — not raw chat walls",
  "Self-hosted on your cluster",
  "Same theme as the signed-in console",
];

/**
 * Compact marketing landing — aligned with Wecrew Copilot console (warm neutral + terracotta).
 */
export function LandingPage({ authed, onNavigate, onSignOut }) {
  const [tab, setTab] = useState("chat");

  function openConsole(page) {
    if (page) setItem("wecrew_entry_page", page, "session");
    onNavigate(authed ? "app" : "signup");
  }

  return (
    <div className="lp">
      <nav className="lp-nav">
        <button type="button" className="lp-logo" onClick={() => onNavigate("landing")}>
          <span className="lp-mark"><span>W</span></span>
          <span className="lp-name">
            We<em>crew</em> Copilot
          </span>
        </button>
        <div className="lp-links">
          <a href="#features">Features</a>
          <a href="#preview">Preview</a>
        </div>
        <div className="lp-nav-actions">
          {authed ? (
            <>
              <button type="button" className="lp-ghost" onClick={onSignOut}>
                Sign out
              </button>
              <button type="button" className="lp-cta" onClick={() => onNavigate("app")}>
                Open console
              </button>
            </>
          ) : (
            <>
              <button type="button" className="lp-ghost" onClick={() => onNavigate("signin")}>
                Sign in
              </button>
              <button type="button" className="lp-cta" onClick={() => onNavigate("signup")}>
                Get started
              </button>
            </>
          )}
        </div>
      </nav>

      <header className="lp-hero">
        <div className="lp-hero-copy">
          <p className="lp-tag">Knowledge copilot for L2 &amp; platform teams</p>
          <h1>
            Answers grounded in <em>your</em> runbooks.
          </h1>
          <p className="lp-desc">
            Upload SOPs, ask in Chat, and get structured steps with citations — plus logs, ITSM, and
            production config guides in one console.
          </p>
          <ul className="lp-highlights">
            {HIGHLIGHTS.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
          <div className="lp-cta-row">
            <button
              type="button"
              className="lp-cta-primary"
              onClick={() => onNavigate(authed ? "app" : "signup")}
            >
              {authed ? "Open console" : "Create account"}
            </button>
            <button type="button" className="lp-ghost" onClick={() => onNavigate("app")}>
              Demo console
            </button>
          </div>
        </div>
        <div className="lp-hero-visual">
          <ScreenshotFrame
            src="/screenshots/console-chat.png"
            alt="Wecrew Copilot Chat — production console"
            className="lp-hero-shot"
          />
        </div>
      </header>

      <section className="lp-section lp-features" id="features">
        <h2 className="lp-section-title">Where to start</h2>
        <p className="lp-section-sub">Same navigation you will see after sign-in.</p>
        <div className="lp-tiles">
          {QUICK_TILES.map((t) => (
            <button
              key={t.id}
              type="button"
              className="lp-tile"
              onClick={() => openConsole(t.page)}
            >
              <span className="lp-tile-icon" aria-hidden>
                {t.icon}
              </span>
              <span className="lp-tile-title">{t.title}</span>
              <span className="lp-tile-desc">{t.desc}</span>
              <span className="lp-tile-go">Open →</span>
            </button>
          ))}
        </div>
      </section>

      <section className="lp-section lp-preview" id="preview">
        <h2 className="lp-section-title">Console preview</h2>
        <p className="lp-section-sub">
          Live UI at <code className="lp-code">kt.copilot.wecrew.in</code>
        </p>
        <div className="lp-tabs" role="tablist">
          {[
            ["chat", "Chat"],
            ["ops", "Logs"],
            ["itsm", "ITSM"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`lp-tab ${tab === id ? "active" : ""}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="lp-showcase-shot" key={tab}>
          <ShowcaseScreenshot tab={tab} />
        </div>
      </section>

      <footer className="lp-foot">
        <span className="lp-foot-brand">
          We<em>crew</em> Copilot AI
        </span>
        <span className="lp-foot-meta">
          API: <code className="lp-code">kt.copilot.api.wecrew.in</code>
        </span>
        <div className="lp-foot-links">
          <button type="button" onClick={() => onNavigate("signin")}>
            Sign in
          </button>
          <button type="button" onClick={() => onNavigate("app")}>
            Console
          </button>
        </div>
        <p className="lp-foot-copy">© {new Date().getFullYear()} Wecrew · Self-hosted knowledge copilot</p>
      </footer>
    </div>
  );
}
